import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppStateCheckResult, AppStateDto, WebAppState } from '../models';
import { VersionService } from './version.service';

@Injectable({ providedIn: 'root' })
export class AppStateService {
  #httpClient = inject(HttpClient);
  #queryClient = inject(QueryClient);
  #versionService = inject(VersionService);

  #systemMessageDismissed = signal(false);

  appStateQuery = injectQuery(() => ({
    queryKey: ['app-state'],
    queryFn: async () => {
      const dto = await lastValueFrom(
        this.#httpClient.get<AppStateDto>(
          `${environment.appStateApiUrl}/api/app-state`
        )
      );
      return dto;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: true,
  }));

  checkResult = computed<AppStateCheckResult>(() => {
    const query = this.appStateQuery;

    if (query.isLoading()) {
      return { status: 'loading' };
    }

    if (query.isError()) {
      return { status: 'error' };
    }

    const dto = query.data();
    if (!dto) {
      return { status: 'loading' };
    }

    const web: WebAppState = {
      version: dto.web,
      maintenance: dto.maintenanceWeb,
      systemMessage: dto.systemMessageWeb,
    };

    if (web.maintenance?.isActive) {
      return {
        status: 'maintenance',
        message: web.maintenance.message || 'Maintenance en cours',
      };
    }

    const currentVersion = this.#versionService.getCurrentVersion();

    if (
      web.version?.minimum &&
      this.#versionService.isUpdateMandatory(currentVersion, web.version.minimum)
    ) {
      return {
        status: 'force-update',
        currentVersion,
        minimumVersion: web.version.minimum,
      };
    }

    const optionalUpdate =
      !!web.version?.latest &&
      this.#versionService.isUpdateOptional(currentVersion, web.version.latest);

    const systemMessage =
      this.#systemMessageDismissed() ? null : (web.systemMessage ?? null);

    return {
      status: 'ok',
      optionalUpdate,
      latestVersion: web.version?.latest ?? null,
      systemMessage,
    };
  });

  refreshAppState(): void {
    this.#queryClient.invalidateQueries({ queryKey: ['app-state'] });
  }

  dismissSystemMessage(): void {
    this.#systemMessageDismissed.set(true);
  }

  setupVisibilityChangeListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.refreshAppState();
      }
    });
  }
}
